import EditIcon from '@mui/icons-material/Edit';
import React from 'react';

import { inject, injectValue } from '../../../../../../../common/container/inject';
import { UserProjectRoleAction } from '../../../../../../../service/projectRole/entity/actions/UserProjectRoleAction';
import { IRootAdminService, RootAdminServiceToken } from '../../../../../../../service/RootAdminService';
import { IRouterService, RouterServiceToken } from '../../../../../../../service/route/IRouterService';
import { UserSystemRoleModelPermissionMap } from '../../../../../../../service/systemRole/entity/actions/UserSystemRoleModelPermissionMap';
import { UserSourceType } from '../../../../../../../service/user/UserSourceType';
import { IMainLayoutDomainStore } from '../../../../../../layout/main/store/domain/IMainLayoutDomainStore';
import { AdminTableDomain } from '../../../../table/store/AdminTableDomain';
import { IAdminTableColumn } from '../../../../table/store/IAdminTableUI';
import { UserProjectRoleTranslationInjection } from './inject/TranslationExtensions';
import { IUserProjectRoleAdminModel } from './UserProjectRoleAdminModel';
import { ILocalization, ILocalizationToken } from '../../../../../../../application/language/ILocalization';

export class UserProjectRoleAdminDomain extends AdminTableDomain<IUserProjectRoleAdminModel, any> {
  constructor(
    layoutDomain: IMainLayoutDomainStore,
    protected rootPrivateServices = inject<IRootAdminService>(RootAdminServiceToken),
    private router: IRouterService = inject<IRouterService>(RouterServiceToken),
    private translationExtensions = injectValue<typeof UserProjectRoleTranslationInjection>(
      UserProjectRoleTranslationInjection,
    ),
    public i18n: ILocalization = inject<ILocalization>(ILocalizationToken)
  ) {
    super(layoutDomain, rootPrivateServices.projectRole.entity);
    this.ui.isInlineEdit.setValue(false);
  }

  setPermissions() {
    super.setPermissions();
    this.ui.isCanCreate.setValue(
      this.layoutDomain.userHaveAnyAccess([UserSystemRoleModelPermissionMap['settings-user-project-role-create']]),
    );
    this.ui.isCanDelete.setValue(
      this.layoutDomain.userHaveAnyAccess([UserSystemRoleModelPermissionMap['settings-user-project-role-delete']]),
    );
    this.ui.isCanEdit.setValue(
      this.layoutDomain.userHaveAnyAccess([UserSystemRoleModelPermissionMap['settings-user-project-role-update']]),
    );
  }

  async loadData() {
    const columns = await this.getColumns();
    this.setActions();

    this.ui.columns.setList(columns);
    await super.loadData();
  }

  private setActions() {
    const actions: any[] = [];
    if (this.layoutDomain.userHaveAnyAccess([UserSystemRoleModelPermissionMap['settings-user-project-role-update']])) {
      actions.push((rowData: any) => ({
        disabled: rowData.sourceType === UserSourceType.activeDirectory,
        icon: () => <EditIcon />,
        tooltip: this.i18n.translate('phrases.edit'),
        onClick: (event, rowData: any) => {
          this.router.goTo(`/settings/user/project/role/${rowData?.id}/edit`);
        },
      }));
    }
    this.ui.actions.setList(actions);
  }

  private async getColumns(): Promise<IAdminTableColumn[]> {
    const translateActions = (actions: { data: { action: string }[] }) => {
      const translations = new Map();
      translations.set(UserProjectRoleAction.updateFieldsProject, this.i18n.translate('projectRoles.editingProjectFields'));
      translations.set(UserProjectRoleAction.updateTeamProject, this.i18n.translate('projectRoles.editingProjectTeams'));
      translations.set(UserProjectRoleAction.creteApplication, this.i18n.translate('projectRoles.creatingNewProfiles'));
      translations.set(UserProjectRoleAction.editApplication, this.i18n.translate('projectRoles.editingProfiles'));
      translations.set(UserProjectRoleAction.reportsAccess, this.i18n.translate('projectRoles.generatingReports'));
      translations.set(UserProjectRoleAction.customRequirementsAccess, this.i18n.translate('projectRoles.creatingRequirements'));
      translations.set(UserProjectRoleAction.acceptCancelRequirementAccess, this.i18n.translate('projectRoles.acceptingAndDeletingRequirements'));
      translations.set(
        UserProjectRoleAction.newMessagesNotification,
        this.i18n.translate('projectRoles.receivingNotificationsOfNewComments')
      );
      translations.set(UserProjectRoleAction.showRegulators, this.i18n.translate('projectRoles.viewingRequirementsOfExternalRegulatoryAuthorities') );
      translations.set(UserProjectRoleAction.jiraIntegration, this.i18n.translate('projectRoles.integratingASWithJira'));
      translations.set(UserProjectRoleAction.showUnverifiedRequirements, this.i18n.translate('projectRoles.managingRequirementsVerification'));
      translations.set(UserProjectRoleAction.writeRequirementRemarks, this.i18n.translate('projectRoles.writeRequirementRemarks') );
      this.translationExtensions.forEach((translation) => {
        translations.set(translation.value, translation.name);
      });

      return actions.data.map((item) => translations.get(item.action)).join(', ');
    };
    return [
      {
        title: this.i18n.translate('phrases.nameTitle'),
        field: 'name',
      },
      {
        title: this.i18n.translate('phrases.description'),
        field: 'description',
      },
      {
        title: this.i18n.translate('phrases.rights'),
        field: 'permission',
        render: (rowData) => <div> {translateActions(rowData.permissions)}</div>,
      },
      // {
      //   title: 'Активен',
      //   field: 'isActive',
      //   type: 'boolean',
      // },
    ];
  }
}
